// web/src/services/engramService.ts
// FRAME — Engram B2B Memory API client
//
// Connects PRISM behavioral data to Engram's persistent memory engine.
// All calls are fire-and-forget with graceful fallback — PRISM works standalone
// if Engram is unavailable. Premium feature (Silver+ tier).

const ENGRAM_API_URL = import.meta.env.VITE_ENGRAM_API_URL || '';
const ENGRAM_API_KEY = import.meta.env.VITE_ENGRAM_API_KEY || '';

const SYNC_TIMESTAMP_KEY = 'frame_engram_last_sync';
const ENGRAM_ENABLED_KEY = 'frame_engram_enabled';

interface EngramIngestResponse {
  node_id: string;
  base_score: number;
  categories: string[];
  updated: boolean;
}

interface EngramPromptConfig {
  name: string;
  template: string;
  capabilities: string[];
  variables: Record<string, string>;
  temperature?: number;
  max_tokens?: number;
}

interface EngramGoal {
  id: string;
  name: string;
  description: string;
  metric_type: 'counter' | 'percentage' | 'threshold' | 'boolean';
  target_value: number;
  current_value: number;
  completed: boolean;
  created_at: string;
  updated_at: string;
}

interface EngramNodeType {
  name: string;
  description: string;
  schema: Record<string, unknown>;
  decay_override?: number;
  scoring_override?: Record<string, number>;
}

interface EngramQueryResponse {
  context: string;
  identity_context?: string;
  weaving_directive?: string;
  nodes_used: number;
  top_categories: string[];
  confidence: number;
  prompt_config?: EngramPromptConfig;
  active_goals?: EngramGoal[];
}

interface EngramUserSummary {
  node_count: number;
  top_categories: string[];
  has_identity: boolean;
  sessions: number;
  affinity: {
    trust_score: number;
    context_welcome: number;
    sessions_tracked: number;
  };
}

interface IngestEvent {
  content: string;
  event_type?: string;
  metadata?: Record<string, unknown>;
  node_type?: string;  // Custom node type (validated against tenant's registered types)
}

async function engramFetch<T>(path: string, options: RequestInit = {}): Promise<T | null> {
  if (!ENGRAM_API_URL || !ENGRAM_API_KEY) return null;
  try {
    const res = await fetch(`${ENGRAM_API_URL}/api/v1${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': ENGRAM_API_KEY,
        ...options.headers,
      },
    });
    if (!res.ok) {
      console.warn(`[Engram] ${res.status} on ${path}`);
      return null;
    }
    return await res.json() as T;
  } catch (err) {
    console.warn('[Engram] Network error:', err);
    return null;
  }
}

export const EngramService = {
  /** Check if Engram sync is enabled by the user. */
  isEnabled(): boolean {
    if (!ENGRAM_API_URL || !ENGRAM_API_KEY) return false;
    try {
      return localStorage.getItem(ENGRAM_ENABLED_KEY) === 'true';
    } catch {
      return false;
    }
  },

  /** Enable or disable Engram sync. */
  setEnabled(enabled: boolean): void {
    try {
      localStorage.setItem(ENGRAM_ENABLED_KEY, String(enabled));
    } catch { /* ignore */ }
  },

  /** Check if Engram API is configured (has URL + key). */
  isConfigured(): boolean {
    return Boolean(ENGRAM_API_URL && ENGRAM_API_KEY);
  },

  /** Ingest a single memory event. Optionally specify a custom node_type. */
  async ingest(userId: string, content: string, eventType?: string, metadata?: Record<string, unknown>, nodeType?: string): Promise<EngramIngestResponse | null> {
    return engramFetch<EngramIngestResponse>('/ingest', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        content,
        event_type: eventType || 'behavioral',
        metadata,
        ...(nodeType ? { node_type: nodeType } : {}),
      }),
    });
  },

  /** Batch ingest multiple events. */
  async batchIngest(userId: string, events: IngestEvent[]): Promise<{ ingested: number } | null> {
    if (events.length === 0) return null;
    // Engram batch max is 100
    const batches = [];
    for (let i = 0; i < events.length; i += 100) {
      batches.push(events.slice(i, i + 100));
    }
    let totalIngested = 0;
    for (const batch of batches) {
      const result = await engramFetch<{ results: EngramIngestResponse[] }>('/ingest/batch', {
        method: 'POST',
        body: JSON.stringify({ user_id: userId, events: batch }),
      });
      if (result?.results) totalIngested += result.results.length;
    }
    return { ingested: totalIngested };
  },

  /** Query Engram for memory context to inject into chat. */
  async query(userId: string, currentInput?: string, options?: {
    exchangeNumber?: number;
    maxTokens?: number;
    includeIdentity?: boolean;
    includeWeaving?: boolean;
  }): Promise<EngramQueryResponse | null> {
    return engramFetch<EngramQueryResponse>('/query', {
      method: 'POST',
      body: JSON.stringify({
        user_id: userId,
        current_input: currentInput,
        exchange_number: options?.exchangeNumber ?? 1,
        max_tokens: options?.maxTokens ?? 500,
        include_identity: options?.includeIdentity ?? true,
        include_weaving: options?.includeWeaving ?? true,
      }),
    });
  },

  /** Get user memory summary from Engram. */
  async getUserSummary(userId: string): Promise<EngramUserSummary | null> {
    return engramFetch<EngramUserSummary>(`/users/${encodeURIComponent(userId)}`);
  },

  // ─── Prompt Configs (Pro+ tier) ──────────────────────────

  /** Get all prompt configs for this tenant. */
  async getPromptConfigs(): Promise<EngramPromptConfig[] | null> {
    const result = await engramFetch<{ configs: EngramPromptConfig[] }>('/config/prompts');
    return result?.configs ?? null;
  },

  /** Get a specific prompt config by name. */
  async getPromptConfig(name: string): Promise<EngramPromptConfig | null> {
    return engramFetch<EngramPromptConfig>(`/config/prompts/${encodeURIComponent(name)}`);
  },

  /** Create or update a prompt config. */
  async upsertPromptConfig(config: EngramPromptConfig): Promise<EngramPromptConfig | null> {
    return engramFetch<EngramPromptConfig>(`/config/prompts/${encodeURIComponent(config.name)}`, {
      method: 'PUT',
      body: JSON.stringify(config),
    });
  },

  // ─── Goals ─────────────────────────────────────────────

  /** Get all goals for this tenant. */
  async getGoals(): Promise<EngramGoal[] | null> {
    const result = await engramFetch<{ goals: EngramGoal[] }>('/goals');
    return result?.goals ?? null;
  },

  /** Create a new goal. */
  async createGoal(goal: { name: string; description: string; metric_type: string; target_value: number }): Promise<EngramGoal | null> {
    return engramFetch<EngramGoal>('/goals', {
      method: 'POST',
      body: JSON.stringify(goal),
    });
  },

  /** Update goal progress. */
  async updateGoalProgress(goalId: string, value: number, increment?: boolean): Promise<EngramGoal | null> {
    return engramFetch<EngramGoal>(`/goals/${encodeURIComponent(goalId)}/progress`, {
      method: 'POST',
      body: JSON.stringify({ value, increment: increment ?? true }),
    });
  },

  // ─── Custom Node Types (Pro+ tier) ─────────────────────

  /** Get all custom node types for this tenant. */
  async getNodeTypes(): Promise<EngramNodeType[] | null> {
    const result = await engramFetch<{ node_types: EngramNodeType[] }>('/node-types');
    return result?.node_types ?? null;
  },

  /** Register a custom node type. */
  async createNodeType(nodeType: EngramNodeType): Promise<EngramNodeType | null> {
    return engramFetch<EngramNodeType>('/node-types', {
      method: 'POST',
      body: JSON.stringify(nodeType),
    });
  },

  // ─── PRISM-Specific Initialization ─────────────────────

  /** Register Frame's custom node types and prompt config on first connection.
   *  Idempotent — safe to call on every app load. */
  async initializePrismIntegration(): Promise<boolean> {
    const INIT_KEY = 'frame_engram_initialized';
    try {
      if (localStorage.getItem(INIT_KEY) === 'true') return true;
    } catch { /* continue */ }

    // Register PRISM-specific node types
    const nodeTypes: EngramNodeType[] = [
      {
        name: 'prism_behavioral',
        description: 'PRISM behavioral signal — user interaction with scored photo',
        schema: { photoId: 'string', signalWeight: 'number', compositeScore: 'number' },
        decay_override: 0.03, // Slower decay for preference signals
      },
      {
        name: 'prism_preference_update',
        description: 'PRISM preference vector snapshot after learning',
        schema: { preferenceVector: 'object', positive_count: 'number', negative_count: 'number' },
        decay_override: 0.01, // Very slow decay — identity-level
      },
      {
        name: 'prism_style_milestone',
        description: 'User reached a style confidence milestone',
        schema: { confidence: 'number', label: 'string', photosSampled: 'number' },
        decay_override: 0.005, // Near-permanent
      },
    ];

    for (const nt of nodeTypes) {
      await this.createNodeType(nt);
    }

    // Register PRISM prompt config
    await this.upsertPromptConfig({
      name: 'prism_photo_advisor',
      template: `You are a personal photo curation advisor powered by PRISM memory.
You understand the user's visual identity — their aesthetic preferences, photo habits,
and self-image goals. Focus on:
- Their evolving style DNA: {{style_summary}}
- Photo curation for {{context}} (dating, professional, social)
- Personal branding through visual storytelling
- How their choices reflect who they are and who they want to be

Confidence level: {{confidence_label}} ({{confidence_pct}}%)
Photos analyzed: {{photos_sampled}}

{{#if active_goals}}
Active goals: {{active_goals}}
{{/if}}`,
      capabilities: ['photo_curation', 'style_analysis', 'personal_branding', 'visual_identity'],
      variables: {
        style_summary: 'Dynamic — populated from preference vector',
        context: 'User-selected scoring mode',
        confidence_label: 'From PRISM confidence ramp',
        confidence_pct: 'Numeric confidence percentage',
        photos_sampled: 'Total photos that contributed to learning',
        active_goals: 'Current visual identity goals',
      },
      temperature: 0.7,
      max_tokens: 1024,
    });

    // Create default PRISM goals
    const defaultGoals = [
      { name: 'Style Discovery', description: 'Score and interact with photos to discover your visual style', metric_type: 'threshold', target_value: 50 },
      { name: 'Photo Curation Pro', description: 'Curate photos across 3 different scoring modes', metric_type: 'counter', target_value: 3 },
    ];
    for (const g of defaultGoals) {
      await this.createGoal(g);
    }

    try {
      localStorage.setItem(INIT_KEY, 'true');
    } catch { /* ignore */ }
    return true;
  },

  /** Get/set last sync timestamp. */
  getLastSyncTime(): number {
    try {
      return parseInt(localStorage.getItem(SYNC_TIMESTAMP_KEY) || '0', 10);
    } catch { return 0; }
  },

  setLastSyncTime(ts: number): void {
    try {
      localStorage.setItem(SYNC_TIMESTAMP_KEY, String(ts));
    } catch { /* ignore */ }
  },
};
